import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, Chip, Checkbox, Paper,
  Stack, Divider, Tooltip, IconButton, FormControlLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tasksAPI } from '../../services/api';
import TaskPhoto from '../../pages/technician/TaskPhoto';
import DriveFolderPicker from './DriveFolderPicker';

/**
 * Bandeja de fotos de obra pendientes de pasar a Drive.
 *
 * Los tecnicos no tienen cuenta de Google, asi que sus fotos aterrizan en el
 * servidor. Aqui un gestor las revisa y las traslada a la carpeta del proyecto
 * con su propia credencial. Al subirlas se borran del servidor.
 *
 * Cuando el proyecto no tiene carpeta de Drive, la ruta de siempre no existe:
 * para esos casos se puede elegir la carpeta destino a mano en el explorador, y
 * opcionalmente dejarla asociada al proyecto para no repetir la eleccion.
 */
export default function PendingPhotos({ onCountChange }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [linkToProject, setLinkToProject] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.pendingPhotos();
      setData(res.data);
      setSelected([]);
      if (onCountChange) onCountChange(res.data.count);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar las fotos.');
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const toggleGroup = (group) => {
    const ids = group.photos.map((p) => p.update_id);
    const allIn = ids.every((id) => selected.includes(id));
    setSelected((prev) => allIn
      ? prev.filter((id) => !ids.includes(id))
      : [...new Set([...prev, ...ids])]);
  };

  const push = async (options = {}) => {
    setBusy(true);
    setResult(null);
    try {
      const res = await tasksAPI.pushPhotosToDrive(selected, options);
      setResult(res.data);
      setPicker(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido subir las fotos.');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  // Sin carpeta de Drive no hay ruta de siempre: esas fotos solo pueden subirse
  // eligiendo el destino a mano
  const selectable = data.groups.filter((g) => g.drive_folder_id);
  const blocked = data.groups.filter((g) => !g.drive_folder_id);
  const blockedIds = blocked.flatMap((g) => g.photos.map((p) => p.update_id));
  const selectedBlocked = selected.filter((id) => blockedIds.includes(id)).length;

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {result && (
        <Alert severity={result.failed ? 'warning' : 'success'} sx={{ mb: 2 }}
          onClose={() => setResult(null)}>
          {result.moved} foto{result.moved === 1 ? '' : 's'} subida{result.moved === 1 ? '' : 's'} a
          Drive{result.target_folder_name ? `, en la carpeta ${result.target_folder_name}` : ''}.
          La copia del servidor se conserva {result.retention_days} dias mas para que el
          tecnico la siga viendo, y luego se borra sola.
          {result.linked_projects?.length > 0 && (
            <>
              <br />Esa carpeta queda asociada como carpeta de proyecto de{' '}
              {result.linked_projects.map((p) => p.project_name).join(', ')}: las proximas fotos
              iran solas a multimedia/&lt;orden&gt;.
            </>
          )}
          {result.failed > 0 && (
            <>
              <br />No se pudieron subir {result.failed}:
              <ul style={{ margin: '4px 0 0 16px' }}>
                {result.details.failed.map((f, i) => <li key={i}>{f.error}</li>)}
              </ul>
            </>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Fotos hechas por los tecnicos, pendientes de pasar a Drive. Van a{' '}
            <strong>multimedia/&lt;orden&gt;</strong> dentro de la carpeta del proyecto.
            La copia del servidor se conserva <strong>{data.retention_days} dias</strong> mas
            y despues se borra sola para liberar espacio.
          </Typography>
          {data.archived_awaiting_purge > 0 && (
            <Typography variant="caption" color="textSecondary">
              {data.archived_awaiting_purge} foto(s) ya en Drive esperando a cumplir el plazo.
            </Typography>
          )}
        </Box>
        <Tooltip title="Actualizar">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Elegir a mano la carpeta de Drive donde dejar las fotos seleccionadas">
          <span>
            <Button variant="outlined" startIcon={<DriveFolderUploadIcon />}
              disabled={!selected.length || busy} onClick={() => setPicker(true)}>
              Elegir carpeta...
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={selectedBlocked
          ? 'Hay fotos de proyectos sin carpeta de Drive: usa "Elegir carpeta..."'
          : 'Van a multimedia/<orden> dentro de la carpeta del proyecto'}>
          <span>
            <Button variant="contained" startIcon={<CloudUploadIcon />}
              disabled={!selected.length || busy || selectedBlocked > 0}
              onClick={() => push()}>
              {busy ? 'Subiendo...' : `Subir ${selected.length || ''} a Drive`}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {data.count === 0 && (
        <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          No hay fotos pendientes. Todas estan ya en Drive.
        </Typography>
      )}

      {blocked.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Hay fotos de proyectos sin carpeta de Drive creada
          ({blocked.map((g) => g.project_name).join(', ')}).
          Selecciona las fotos y usa <strong>Elegir carpeta...</strong> para dejarlas en una ruta
          concreta de Drive, o crea antes la carpeta del proyecto.
        </Alert>
      )}

      {[...selectable, ...blocked].map((group) => {
        const ids = group.photos.map((p) => p.update_id);
        const allIn = ids.every((id) => selected.includes(id));
        return (
          <Paper key={`${group.project_id}-${group.manufacturing_order_id}`}
            variant="outlined" sx={{ mb: 2 }}>
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                       backgroundColor: '#fafafa', flexWrap: 'wrap' }}>
              <Checkbox checked={allIn} onChange={() => toggleGroup(group)} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {group.project_code ? `${group.project_code} — ` : ''}{group.project_name}
                </Typography>
                <Typography variant="caption" color="textSecondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FolderIcon fontSize="inherit" />
                  {group.drive_folder_id ? group.target_folder : 'Destino a elegir a mano'}
                  {group.manufacturing_order_code ? ` · ${group.manufacturing_order_code}` : ''}
                </Typography>
              </Box>
              <Chip size="small" label={`${group.photos.length} foto${group.photos.length === 1 ? '' : 's'}`} />
              {!group.drive_folder_id && (
                <Chip size="small" color="warning" label="Sin carpeta de Drive" />
              )}
            </Box>
            <Divider />
            <Box sx={{ p: 1.5, display: 'grid',
                       gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
              {group.photos.map((p) => (
                <Box key={p.update_id}
                  sx={{ border: '2px solid',
                        borderColor: selected.includes(p.update_id) ? 'primary.main' : 'transparent',
                        borderRadius: 1, p: 0.5, cursor: 'pointer' }}
                  onClick={() => toggle(p.update_id)}>
                  <TaskPhoto filename={p.photo_path} height={110} clickable={false} />
                  <Typography variant="caption" display="block" noWrap sx={{ mt: 0.5 }}>
                    {p.task_code}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" noWrap>
                    {p.task_title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {p.created_at?.slice(0, 16).replace('T', ' ')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })}

      <DriveFolderPicker
        open={picker}
        onClose={() => setPicker(false)}
        title={`Carpeta destino de ${selected.length} foto${selected.length === 1 ? '' : 's'}`}
        description={
          'Navega hasta la carpeta de Drive donde quieres dejar las fotos. Van dentro tal cual, '
          + 'sin crear subcarpetas, con el nombre <codigo tarea>_<fecha>.'
        }
        onSelect={(folderId) => push({ targetFolderId: folderId, linkToProject })}
        confirmLabel="Subir aqui"
        footer={
          <FormControlLabel
            control={<Checkbox size="small" checked={linkToProject}
              onChange={(e) => setLinkToProject(e.target.checked)} />}
            label={
              <Typography variant="body2">
                Dejar esta carpeta asociada al proyecto, para que las proximas fotos vayan solas
                (solo se aplica a los proyectos que aun no tienen carpeta)
              </Typography>
            }
          />
        }
      />
    </Box>
  );
}
